'use client';

import { useState, useEffect, useMemo } from 'react';
import { useRouter } from 'next/navigation';

export default function PricingPlans() {
  const [isYearly, setIsYearly] = useState(false);
  const [plans, setPlans] = useState([]);
  const [seats, setSeats] = useState(1);
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const router = useRouter();
  const BASE_URL = process.env.NEXT_PUBLIC_API_BASE_URL;

  useEffect(() => {
    async function fetchPlans() {
      try {
        const res = await fetch(`${BASE_URL}/admin/subscription-rules`);
        const data = await res.json();
        if (data?.data) setPlans(data.data);
      } catch (error) {
        console.error('Failed to fetch plans:', error);
      }
    }
    fetchPlans();
  }, [BASE_URL]);

  const yearlyPlans = plans.filter((p) => p.plan_type === 'annual');
  const monthlyPlans = plans.filter(
    (p) => p.plan_type === 'monthly' || p.plan_type === 'six_month'
  );
  const currentPlans = isYearly ? yearlyPlans : monthlyPlans;

  useEffect(() => {
    if (!selectedPlanId && currentPlans[0]) {
      setSelectedPlanId(currentPlans[0].id);
    }
  }, [currentPlans, selectedPlanId]);

  const selectedPlan = useMemo(
    () => currentPlans.find((p) => p.id === selectedPlanId) || currentPlans[0],
    [currentPlans, selectedPlanId]
  );

  // Pricing calculations in Naira (NGN)
  const baseRate = isYearly ? 675 : 750;
  const pricePerSeat = Number(selectedPlan?.price_per_license) || baseRate;
  const estimatedTotal = (pricePerSeat * seats).toFixed(2);

  const incrementSeats = () => setSeats((s) => Math.min(s + 1, 9999));
  const decrementSeats = () => setSeats((s) => Math.max(s - 1, 1));
  const handleSeatsInput = (e) => {
    const val = parseInt(e.target.value, 10);
    setSeats(Number.isNaN(val) || val < 1 ? 1 : val);
  };

  const handleContinue = () => {
    const params = new URLSearchParams({
      plan: String(selectedPlan?.id ?? 'professional'),
      licenses: String(seats),
      billing: isYearly ? 'annual' : 'monthly',
      total: estimatedTotal,
    });
    router.push(`/pricing/pricing-and-subscription/subscription-details?${params.toString()}`);
  };

  const cardConfig = [
    {
      key: 'free',
      label: 'FREE TRIAL',
      price: 'Free',
      priceColor: '#c1402c',
      unit: '',
      subtitle: 'Get started at no cost',
      cardBg: '#ffffff',
      textColor: '#111827',
      subTextColor: '#6b7280',
      cta: { label: 'Start Free Trial', style: 'light' },
      selectable: false,
      features: [
        { label: '1 employee only', enabled: true },
        { label: 'Core payroll features', enabled: true },
        { label: 'Basic reporting', enabled: true },
        { label: 'Email support', enabled: true },
        { label: 'No pay schedule', enabled: false },
        { label: 'Read-only after trial', enabled: false },
        { label: 'Single user', enabled: false },
      ],
    },
    {
      key: 'professional',
      label: 'PROFESSIONAL',
      price: `₦${isYearly ? '675' : '750'}`,
      priceColor: '#ffffff',
      unit: '/seat/mo',
      subtitle: 'Full payroll suite for growing teams',
      cardBg: '#232323',
      textColor: '#ffffff',
      subTextColor: '#9ca3af',
      highlighted: true,
      cta: { label: 'Get Started', style: 'filled' },
      selectable: true,
      planIndex: 0,
      features: [
        { label: 'Unlimited employees', enabled: true },
        { label: 'Pay schedule management', enabled: true },
        { label: 'Advanced reporting', enabled: true },
        { label: 'Priority support', enabled: true },
        { label: 'Multi-user access', enabled: true },
        { label: 'Compliance tools', enabled: true },
      ],
    },
    {
      key: 'enterprise',
      label: 'ENTERPRISE',
      price: `₦${isYearly ? 'x' : 'x'}`,
      priceColor: '#c1402c',
      unit: '/seat/mo',
      subtitle: 'Custom solutions for large organizations',
      cardBg: '#ffffff',
      textColor: '#111827',
      subTextColor: '#6b7280',
      cta: { label: 'Contact Sales', style: 'light' },
      selectable: false,
      features: [
        { label: 'Everything in Professional', enabled: true },
        { label: 'Dedicated account manager', enabled: true },
        { label: 'Custom integrations', enabled: true },
        { label: 'SLA guarantee', enabled: true },
        { label: 'Audit logs', enabled: true },
        { label: 'SSO / SAML', enabled: true },
      ],
    },
  ];

  return (
    <main style={{ backgroundColor: '#f8fafc', minHeight: '100vh', padding: '3.5rem 1rem 6rem' }}>
      {/* ===== HEADER SECTION ===== */}
      <div className="text-center px-3 mb-4">
        <span
          style={{
            display: 'inline-block',
            backgroundColor: '#eef4ff',
            color: '#2563eb',
            fontSize: '0.75rem',
            fontWeight: 700,
            letterSpacing: '0.06em',
            padding: '4px 16px',
            borderRadius: '999px',
            marginBottom: '1rem',
          }}
        >
          PRICING
        </span>

        <h1
          style={{
            fontSize: 'clamp(2rem, 4.5vw, 2.55rem)',
            fontWeight: 800,
            color: '#c1402c',
            letterSpacing: '-0.025em',
            margin: '0 0 0.5rem',
          }}
        >
          Simple, transparent pricing
        </h1>
        <p
          style={{
            color: '#6b7280',
            fontSize: '1rem',
            marginBottom: '2rem',
          }}
        >
          Choose the plan that fits your team. No hidden fees.
        </p>

        {/* Monthly / Yearly Toggle */}
        <div className="d-flex justify-content-center">
          <div
            className="d-flex align-items-center"
            style={{
              backgroundColor: '#ffffff',
              borderRadius: '999px',
              padding: '4px',
              boxShadow: '0 2px 10px rgba(0,0,0,0.06)',
            }}
          >
            <button
              onClick={() => setIsYearly(false)}
              className="btn btn-sm rounded-pill"
              style={{
                backgroundColor: !isYearly ? '#fdece7' : 'transparent',
                color: !isYearly ? '#c1402c' : '#6b7280',
                fontWeight: 600,
                fontSize: '0.85rem',
                padding: '0.45rem 1.35rem',
                border: 'none',
                transition: 'all 0.2s ease',
              }}
            >
              Monthly
            </button>
            <button
              onClick={() => setIsYearly(true)}
              className="btn btn-sm rounded-pill d-flex align-items-center gap-2"
              style={{
                backgroundColor: isYearly ? '#fdece7' : 'transparent',
                color: isYearly ? '#c1402c' : '#6b7280',
                fontWeight: 600,
                fontSize: '0.85rem',
                padding: '0.45rem 1.35rem',
                border: 'none',
                transition: 'all 0.2s ease',
              }}
            >
              Yearly
              <span
                style={{
                  backgroundColor: '#dcfce7',
                  color: '#16a34a',
                  fontSize: '0.72rem',
                  fontWeight: 700,
                  borderRadius: '999px',
                  padding: '2px 8px',
                }}
              >
                -25%
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* ===== 3 PRICING CARDS ===== */}
      <div className="container px-3" style={{ maxWidth: '1040px' }}>
        <div className="row justify-content-center g-4 align-items-stretch">
          {cardConfig.map((card) => {
            const backingPlan =
              card.selectable && currentPlans.length
                ? currentPlans[card.planIndex] ?? currentPlans[0]
                : null;
            const displayPrice = backingPlan
              ? `₦${Number(backingPlan.price_per_license).toLocaleString()}`
              : card.price;

            return (
              <div key={card.key} className="col-12 col-md-4 d-flex">
                <div
                  className="w-100 d-flex flex-column"
                  style={{
                    backgroundColor: card.cardBg,
                    color: card.textColor,
                    borderRadius: '20px',
                    padding: '2.25rem 2rem',
                    boxShadow: card.highlighted
                      ? '0 12px 35px rgba(0,0,0,0.25)'
                      : '0 4px 20px rgba(0,0,0,0.04)',
                    transform: card.highlighted ? 'translateY(-6px)' : 'none',
                    border:
                      card.selectable && backingPlan?.id === selectedPlanId
                        ? '2px solid #c1402c'
                        : '1px solid rgba(0,0,0,0.05)',
                    transition: 'transform 0.2s ease, border-color 0.2s ease',
                  }}
                  onClick={() => {
                    if (card.selectable && backingPlan) setSelectedPlanId(backingPlan.id);
                  }}
                >
                  {/* Eyebrow */}
                  <p
                    className="mb-2"
                    style={{
                      fontSize: '0.75rem',
                      fontWeight: 700,
                      letterSpacing: '0.06em',
                      color: card.subTextColor,
                      textTransform: 'uppercase',
                    }}
                  >
                    {card.label}
                  </p>

                  {/* Price Row */}
                  <div className="d-flex align-items-baseline gap-1 mb-1">
                    <span
                      style={{
                        fontSize: '2.4rem',
                        fontWeight: 800,
                        color: card.priceColor,
                        letterSpacing: '-0.02em',
                        lineHeight: 1.15,
                      }}
                    >
                      {displayPrice}
                    </span>
                    {card.unit && (
                      <span
                        style={{
                          color: card.subTextColor,
                          fontSize: '0.88rem',
                          fontWeight: 400,
                        }}
                      >
                        {card.unit}
                      </span>
                    )}
                  </div>

                  {/* Subtitle */}
                  <p
                    style={{
                      color: card.subTextColor,
                      fontSize: '0.85rem',
                      minHeight: '2.5em',
                      lineHeight: 1.45,
                      marginBottom: '1.25rem',
                    }}
                  >
                    {card.subtitle}
                  </p>

                  {/* CTA Button */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (card.key === 'free') {
                        router.push('/pricing/pricing-and-subscription/subscription-details');
                      } else if (card.key === 'professional') {
                        const el = document.getElementById('license-calculator');
                        if (el) el.scrollIntoView({ behavior: 'smooth' });
                      } else {
                        router.push('/contact');
                      }
                    }}
                    className="btn w-100 mb-4"
                    style={
                      card.cta.style === 'filled'
                        ? {
                            backgroundColor: '#c1402c',
                            color: '#ffffff',
                            borderRadius: '10px',
                            padding: '0.65rem 1rem',
                            fontWeight: 600,
                            fontSize: '0.92rem',
                            border: 'none',
                            transition: 'background-color 0.2s ease',
                          }
                        : {
                            backgroundColor: '#f1f5f9',
                            color: '#c1402c',
                            borderRadius: '10px',
                            padding: '0.65rem 1rem',
                            fontWeight: 600,
                            fontSize: '0.92rem',
                            border: 'none',
                            transition: 'background-color 0.2s ease',
                          }
                    }
                    onMouseEnter={(e) => {
                      if (card.cta.style === 'filled') {
                        e.currentTarget.style.backgroundColor = '#a82e1d';
                      } else {
                        e.currentTarget.style.backgroundColor = '#e2e8f0';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (card.cta.style === 'filled') {
                        e.currentTarget.style.backgroundColor = '#c1402c';
                      } else {
                        e.currentTarget.style.backgroundColor = '#f1f5f9';
                      }
                    }}
                  >
                    {card.cta.label}
                  </button>

                  {/* Checklist */}
                  <ul className="list-unstyled mb-0 d-flex flex-column gap-2 flex-grow-1">
                    {card.features.map((f) => (
                      <li
                        key={f.label}
                        className="d-flex align-items-center gap-2"
                        style={{
                          fontSize: '0.85rem',
                          color: f.enabled
                            ? card.textColor
                            : card.highlighted
                            ? 'rgba(255,255,255,0.35)'
                            : '#9ca3af',
                          fontWeight: f.enabled ? 500 : 400,
                        }}
                      >
                        {f.enabled ? (
                          <i
                            className="bi bi-check"
                            style={{
                              color: '#22c55e',
                              fontSize: '1.2rem',
                              lineHeight: 1,
                              fontWeight: 'bold',
                            }}
                          ></i>
                        ) : (
                          <span
                            style={{
                              width: '15px',
                              height: '15px',
                              borderRadius: '50%',
                              backgroundColor: '#e2e8f0',
                              color: '#94a3b8',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '0.75rem',
                              fontWeight: 'bold',
                              lineHeight: 1,
                            }}
                          >
                            &times;
                          </span>
                        )}
                        <span>{f.label}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            );
          })}
        </div>

        {/* ===== LICENSE CALCULATOR CARD ===== */}
        <div
          id="license-calculator"
          className="bg-white mx-auto mt-5"
          style={{
            maxWidth: '540px',
            borderRadius: '20px',
            border: '1px solid #eef2f6',
            boxShadow: '0 4px 25px rgba(0, 0, 0, 0.04)',
            padding: '2.25rem 2.5rem',
          }}
        >
          <h3
            style={{
              fontSize: '1.15rem',
              fontWeight: 700,
              color: '#c1402c',
              marginBottom: '4px',
            }}
          >
            How many licenses do you need?
          </h3>
          <p
            style={{
              color: '#6b7280',
              fontSize: '0.88rem',
              marginBottom: '1.5rem',
            }}
          >
            Each license covers one active employee seat.
          </p>

          {/* Stepper Input Row */}
          <div className="d-flex align-items-center mb-4">
            <div
              className="d-flex align-items-center justify-content-between flex-grow-1"
              style={{
                border: '1.5px solid #e5e7eb',
                borderRadius: '10px',
                padding: '10px 18px',
                backgroundColor: '#ffffff',
              }}
            >
              <input
                type="number"
                min="1"
                max="9999"
                value={seats}
                onChange={handleSeatsInput}
                style={{
                  border: 'none',
                  outline: 'none',
                  fontSize: '1.25rem',
                  fontWeight: 700,
                  color: '#c1402c',
                  width: '90px',
                  backgroundColor: 'transparent',
                }}
              />
              <span style={{ color: '#9ca3af', fontSize: '0.95rem' }}>seats</span>
            </div>

            {/* Stepper Buttons */}
            <div className="d-flex flex-column gap-1 ms-2">
              <button
                type="button"
                onClick={incrementSeats}
                className="btn d-flex align-items-center justify-content-center p-0"
                style={{
                  width: '38px',
                  height: '24px',
                  backgroundColor: '#f1f5f9',
                  borderRadius: '6px',
                  border: 'none',
                  color: '#374151',
                  fontSize: '16px',
                  fontWeight: 700,
                  transition: 'background-color 0.2s ease',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#e2e8f0')}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#f1f5f9')}
              >
                +
              </button>
              <button
                type="button"
                onClick={decrementSeats}
                className="btn d-flex align-items-center justify-content-center p-0"
                style={{
                  width: '38px',
                  height: '24px',
                  backgroundColor: '#f1f5f9',
                  borderRadius: '6px',
                  border: 'none',
                  color: '#374151',
                  fontSize: '16px',
                  fontWeight: 700,
                  transition: 'background-color 0.2s ease',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#e2e8f0')}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#f1f5f9')}
              >
                -
              </button>
            </div>
          </div>

          {/* Estimated Total */}
          <div className="d-flex justify-content-between align-items-baseline mb-4">
            <span style={{ color: '#6b7280', fontSize: '0.95rem' }}>Estimated total</span>
            <div>
              <span
                style={{
                  fontSize: '1.65rem',
                  fontWeight: 800,
                  color: '#c1402c',
                  letterSpacing: '-0.02em',
                }}
              >
                ₦{Number(estimatedTotal).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
              <span style={{ color: '#6b7280', fontSize: '0.9rem', marginLeft: '2px' }}>/mo</span>
            </div>
          </div>

          {/* Continue Button */}
          <button
            onClick={handleContinue}
            className="btn w-100 d-flex align-items-center justify-content-center gap-2"
            style={{
              backgroundColor: '#c1402c',
              color: '#ffffff',
              borderRadius: '10px',
              padding: '13px 20px',
              fontWeight: 600,
              fontSize: '0.96rem',
              border: 'none',
              transition: 'background-color 0.2s ease',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#a82e1d')}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#c1402c')}
          >
            <span>Continue</span>
            <span>&rarr;</span>
          </button>
        </div>
      </div>
    </main>
  );
}